import {
  apiGetUserAdminPermissions,
  getTranslationsList,
  getUser,
} from "@app/api";
import { REGEX, resetIsContinuousLoading } from "@app/helpers";
import { setIsLoading, setShowAlert, store } from "@app/store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Grid,
  useMediaQuery,
  Menu,
  TextField,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import {
  DataDialog,
  DataPagination,
  DataTable,
  Helmet,
  PageTitle,
} from "@app/components/atoms";
import {
  IconActionOverflow,
  IconDelete,
  IconEdit,
  IconPlus,
  IconWarningFormEdit,
} from "@app/components/icons";
import { theme } from "@app/types";
import { InputText } from "primereact/inputtext";
import {
  apiBulkUpdateTranslations,
  apiCreateTranslation,
  apiDeleteTranslation,
  apiDownloadTranslationsSqlApi,
  apiDownloadTranslationsSqlWeb,
  apiDownloadTranslationsSqlMobile,
  apiDownloadTranslationsXlsx,
  apiDownloadTranslationsZip,
  apiUpdateTranslation,
  apiImportDBTranslations,
} from "@app/api/announcements/announcements.api";
import {
  DownloadOutlined,
  MoreVert,
  UploadOutlined,
} from "@mui/icons-material";
import { CURRENT_PAGE, LIMIT_PAGE, TOTAL_PAGE } from "@app/constants";
import { format } from "date-fns";
import en from "@app/locale/en.json";
import ja from "@app/locale/ja.json";
import es from "@app/locale/es.json";
import pt from "@app/locale/pt.json";
import ar from "@app/locale/ar.json";
import ur from "@app/locale/ur.json";
import th from "@app/locale/th.json";
import fr from "@app/locale/fr.json";
import vi from "@app/locale/vi.json";
import lo from "@app/locale/lo.json";
import km from "@app/locale/km.json";
import ru from "@app/locale/ru.json";
import * as XLSX from "xlsx";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Checkbox, FormControlLabel } from "@mui/material";

interface PageInfo {
  total: number;
  page: number;
  limit: number;
}

type TranslationFormModel = {
  translationKey: string;
  english: string;
  japanease: string;
  spanish: string;
  portuguese: string;
  arabic: string;
  urdu: string;
  thai: string;
  french: string;
  vietnamese: string;
  lao: string;
  khmer: string;
  russian: string;
  isVerified: boolean;
};

const PLATFORM_OPTIONS = [
  { id: "WEB", label: "Web" },
  { id: "API", label: "API" },
  { id: "MOBILE", label: "Mobile" },
  { id: "DB", label: "Database" },
];

export const PLATFORM = {
  WEB: "WEB",
  API: "API",
  MOBILE: "MOBILE",
  DB: "DB",
} as const;

export type PlatformType = (typeof PLATFORM)[keyof typeof PLATFORM];

const TRANSLATION_FIELDS: {
  key: keyof TranslationFormModel;
  label: string;
  full?: boolean;
  rtl?: boolean;
}[] = [
  { key: "english", label: "translation.english" },
  { key: "japanease", label: "translation.japanese" },
  { key: "spanish", label: "translation.spanish" },
  { key: "portuguese", label: "translation.portuguese" },
  { key: "arabic", label: "translation.arabic", rtl: true },
  { key: "urdu", label: "translation.urdu", rtl: true },
  { key: "thai", label: "translation.thai" },
  { key: "french", label: "translation.french" },
  { key: "vietnamese", label: "translation.vietnamese" },
  { key: "lao", label: "translation.lao" },
  { key: "khmer", label: "translation.khmer" },
  { key: "russian", label: "translation.russian" },
];

const TRANSLATION_LANGUAGE_KEYS: (keyof TranslationFormModel)[] = [
  "english",
  "japanease",
  "spanish",
  "portuguese",
  "arabic",
  "urdu",
  "thai",
  "french",
  "vietnamese",
  "lao",
  "khmer",
  "russian",
];

export const PLATFORM_ACTION = {
  JSON: "JSON",
  EXCEL: "EXCEL",
  SCRIPT: "SCRIPT",
  ADD: "ADD",
  UPDATE: "UPDATE",
} as const;

export type PlatformActionType =
  (typeof PLATFORM_ACTION)[keyof typeof PLATFORM_ACTION];

export const SORT_ORDER = {
  ASC: "ASC",
  DESC: "DESC",
} as const;

export type SortOrderType = (typeof SORT_ORDER)[keyof typeof SORT_ORDER];

export const DEFAULT_SORT_ORDER: SortOrderType = SORT_ORDER.DESC;

const DEFAULT_SEARCH_TEXT = "";
const DEFAULT_SEARCH_INPUT = "";
const DEFAULT_PLATFORM_FILTER = "all";
const SMALL_SCREEN_BREAKPOINT = "sm";
const DEFAULT_DIALOG_TITLE = "";
const DEFAULT_TIME_FORMAT = "dd/MM/yyyy";
const DEFAULT_ORDER_BY = "createdAt";

export function ManageTranslations() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [bulkTranslationsPreviewOpen, setBulkTranslationsPreviewOpen] =
    useState(false);
  const [bulkPreviewRows, setBulkPreviewRows] = useState<any[]>([]);
  const isSmallScreen = useMediaQuery(
    theme.breakpoints.down(SMALL_SCREEN_BREAKPOINT),
  );
  const [platform, setPlatform] = useState<string>(DEFAULT_PLATFORM_FILTER);
  const [searchInput, setSearchInput] = useState(DEFAULT_SEARCH_INPUT);
  const [searchText, setSearchText] = useState(DEFAULT_SEARCH_TEXT);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [canAddTranslation, setCanAddTranslation] = useState(false);
  const [canUseOptions, setCanUseOptions] = useState(false);
  const [permissionLoaded, setPermissionLoaded] = useState(false);
  const [anchorEl1, setAnchorEl1] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl1);
  const [openPlatformDialog, setOpenPlatformDialog] = useState(false);
  const [platformDialogTitle, setPlatformDialogTitle] =
    useState(DEFAULT_DIALOG_TITLE);
  const [dialogPlatform, setDialogPlatform] = useState<PlatformType>(
    PLATFORM.WEB,
  );
  const [platformButtonType, setPlatformButtonType] =
    useState<PlatformActionType | null>(null);
  const [timeFormat, setTimeFormat] = useState<string>(DEFAULT_TIME_FORMAT);
  const [page, setPage] = useState(TOTAL_PAGE);
  const [limit] = useState(LIMIT_PAGE);
  const [order, setOrder] = useState<SortOrderType>(DEFAULT_SORT_ORDER);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [rows, setRows] = useState<any[]>([]);
  const [bulkSearch, setBulkSearch] = useState("");
  const [openTranslationDialog, setOpenTranslationDialog] = useState(false);
  const [translationDialogTitle, setTranslationDialogTitle] = useState("");
  const [translationDialogButton, setTranslationDialogButton] = useState("");
  const [rowActionAnchor, setRowActionAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const openRowMenu = Boolean(rowActionAnchor);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTranslationDialog, setDeleteTranslationDialog] = useState(false);
  const [bulkUpdateTranslationsInfo, setBulkUpdateTranslationsInfo] =
    useState(false);
  const [bulkUpdateFailedCount, setBulkUpdateFailedCount] = useState(0);
  const savedScrollPosition = useRef<number>(0);
  const shouldPreserveScroll = useRef<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);

  const handleClose = () => {
    setAnchorEl1(null);
  };

  const [pageInfo, setPageInfo] = useState<PageInfo>({
    total: TOTAL_PAGE,
    page: CURRENT_PAGE,
    limit: LIMIT_PAGE,
  });

  const [bulkPaginationModel, setBulkPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const filteredBulkRows = useMemo(
    () =>
      bulkPreviewRows.filter((row) => {
        if (!bulkSearch.trim()) return true;
        const q = bulkSearch.toLowerCase();
        return Object.values(row).some((val) =>
          String(val ?? "")
            .toLowerCase()
            .includes(q),
        );
      }),
    [bulkPreviewRows, bulkSearch],
  );

  const duplicateCount = useMemo(
    () => filteredBulkRows.filter((r) => r._isDuplicate).length,
    [filteredBulkRows],
  );

  const hasDuplicateRows = duplicateCount > 0;

  const REQUIRED_COLUMNS = [
    "Translation ID",
    "Platform",
    "Translation Key",
    "Is Verified",
  ];

  const bulkPreviewColumns: GridColDef[] = useMemo(() => [
    {
      field: "translationId",
      headerName: t("translationId"),
      width: 180,
      headerAlign: "center",
      align: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "platform",
      headerName: t("announcementTable.platform"),
      width: 110,
      headerAlign: "center",
      align: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "translationKey",
      headerName: t("translation.key"),
      width: 220,
      headerAlign: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "english",
      headerName: t("translation.english"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "japanease",
      headerName: t("translation.japanese"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "spanish",
      headerName: t("translation.spanish"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "portuguese",
      headerName: t("translation.portuguese"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "arabic",
      headerName: t("translation.arabic"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
      renderCell: (params) => (
        <div dir="rtl" style={{ textAlign: "right", width: "100%" }}>
          {params.value || "-"}
        </div>
      ),
    },
    {
      field: "urdu",
      headerName: t("translation.urdu"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
      renderCell: (params) => (
        <div dir="rtl" style={{ textAlign: "right", width: "100%" }}>
          {params.value || "-"}
        </div>
      ),
    },
    {
      field: "thai",
      headerName: t("translation.thai"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "french",
      headerName: t("translation.french"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "vietnamese",
      headerName: t("translation.vietnamese"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "lao",
      headerName: t("translation.lao"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "khmer",
      headerName: t("translation.khmer"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "russian",
      headerName: t("translation.russian"),
      width: 200,
      headerAlign: "center",
      headerClassName: "bulk-header",
    },
    {
      field: "isVerified",
      headerName: t("translation.isVerified"),
      width: 200,
      headerAlign: "center",
      align: "center",
      headerClassName: "bulk-header",
      renderCell: (params) => (
        <span
          style={{
            color: params.value === true ? "#0e7c47" : "#991B1B",
            fontWeight: 600,
          }}
        >
          {params.value === true ? "Yes" : "No"}
        </span>
      ),
    },
    {
      field: "_failureReason",
      headerName: t("failureReason"),
      width: 300,
      headerAlign: "center",
      headerClassName: "bulk-header",
      renderCell: (params) => {
        if (!params.value) {
          return (
            <div
              style={{
                width: "100%",
                textAlign: "center",
                color: "#9CA3AF",
                fontWeight: 500,
              }}
            >
              -
            </div>
          );
        }

        return (
          <span style={{ color: "#991B1B", fontWeight: 500 }}>
            {params.value}
          </span>
        );
      },
    },
  ], [t]);

  const [form, setForm] = useState<TranslationFormModel>({
    translationKey: "",
    english: "",
    japanease: "",
    spanish: "",
    portuguese: "",
    arabic: "",
    urdu: "",
    thai: "",
    french: "",
    vietnamese: "",
    lao: "",
    khmer: "",
    russian: "",
    isVerified: false,
  });

  const isUpdateMode = translationDialogButton !== t("formAction.add");

  const [formErrors, setFormErrors] = useState<
    Record<keyof TranslationFormModel, string | null>
  >({
    translationKey: null,
    english: null,
    japanease: null,
    spanish: null,
    portuguese: null,
    arabic: null,
    urdu: null,
    thai: null,
    french: null,
    vietnamese: null,
    lao: null,
    khmer: null,
    russian: null,
    isVerified: null,
  });

  const renderValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === "") {
      return <span style={{ color: "#000", opacity: 1 }}>-</span>;
    }
    return <span style={{ color: "#000", opacity: 1 }}>{value}</span>;
  }, []);

  const renderRtl = useCallback((value: any) => {
    if (value === null || value === undefined || value === "") return "-";
    return (
      <span dir="rtl" style={{ textAlign: "right", display: "block" }}>
        {value}
      </span>
    );
  }, []);

  const columns = useMemo(() => [
    {
      key: "translationKey",
      title: "translation.key",
      dataIndex: "translationKey",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 250 },
      render: renderValue,
    },
    {
      key: "platform",
      title: "announcementTable.platform",
      dataIndex: "platform",
      sorted: true,
      align: "center",
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 200 },
      render: renderValue,
    },
    {
      key: "english",
      title: "translation.english",
      dataIndex: "english",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 220 },
      render: renderValue,
    },
    {
      key: "japanease",
      title: "translation.japanese",
      dataIndex: "japanease",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 220 },
      render: renderValue,
    },
    {
      key: "spanish",
      title: "translation.spanish",
      dataIndex: "spanish",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 220 },
      render: renderValue,
    },
    {
      key: "portuguese",
      title: "translation.portuguese",
      dataIndex: "portuguese",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 250 },
      render: renderValue,
    },
    {
      key: "arabic",
      title: "translation.arabic",
      dataIndex: "arabic",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 220 },
      render: renderRtl,
    },
    {
      key: "urdu",
      title: "translation.urdu",
      dataIndex: "urdu",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 220 },
      render: renderRtl,
    },
    {
      key: "thai",
      title: "translation.thai",
      dataIndex: "thai",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 220 },
      render: renderValue,
    },
    {
      key: "french",
      title: "translation.french",
      dataIndex: "french",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 220 },
      render: renderValue,
    },
    {
      key: "vietnamese",
      title: "translation.vietnamese",
      dataIndex: "vietnamese",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 250 },
      render: renderValue,
    },
    {
      key: "lao",
      title: "translation.lao",
      dataIndex: "lao",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 220 },
      render: renderValue,
    },
    {
      key: "khmer",
      title: "translation.khmer",
      dataIndex: "khmer",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 220 },
      render: renderValue,
    },
    {
      key: "russian",
      title: "translation.russian",
      dataIndex: "russian",
      sorted: true,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      style: { minWidth: 220 },
      render: renderValue,
    },
    {
      key: "isVerified",
      title: "translation.isVerified",
      dataIndex: "translationId",
      sorted: false,
      tableHeadingWhiteSpace: "nowrap",
      className: "txt-pre-wrap",
      align: "center",
      headerAlign: "center",
      style: { minWidth: 220 },
      render: (_: any, record: any) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            fontWeight: 600,
            color: record.isVerified === true ? "#0e7c47" : "#991B1B",
          }}
        >
          {record.isVerified === true ? "Yes" : "No"}
        </div>
      ),
    },

    {
      key: "translationId",
      title: "",
      dataIndex: "translationId",
      render: (_: string, record: any) => (
        <Button
          className="bnt-action-form"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setRowActionAnchor(e.currentTarget);
            setSelectedRow(record);
          }}
        >
          <IconActionOverflow />
        </Button>
      ),
      className: "add",
      width: 0,
      align: "center",
    },
  ], [t, renderValue, renderRtl]);

  const handleSort = (currentOrder: string | undefined, property: string) => {
    const isAsc = orderBy === property && order === SORT_ORDER.ASC;
    setOrder(isAsc ? SORT_ORDER.DESC : SORT_ORDER.ASC);
    setOrderBy(property);
    setPage(TOTAL_PAGE);
  };

  const handleChangePage = (p: number) => {
    setPage(p);
  };

  const fetchTranslations = useCallback(async () => {
    try {
      setTableLoading(true);

      if (shouldPreserveScroll.current) {
        const tableContainer = document.querySelector(
          ".MuiTableContainer-root",
        ) as HTMLElement;

        if (tableContainer) {
          savedScrollPosition.current = tableContainer.scrollTop;
        }
      }

      const res = await getTranslationsList(
        page.toString(),
        limit.toString(),
        order,
        orderBy,
        searchText,
        platform,
      );

      setRows(res.data.data);
      setPageInfo(res.data.meta.pageInfo);
      shouldPreserveScroll.current = false;
    } catch (error) {
      console.error("Error fetching translations:", error);
    } finally {
      setTableLoading(false);
    }
  }, [page, limit, order, orderBy, searchText, platform]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl1(event.currentTarget);
  };

  const fetchPermissions = useCallback(async () => {
    try {
      const perms = (await apiGetUserAdminPermissions())?.data?.data;

      const canRead =
        perms?.TRANSLATIONS_ADMIN === true ||
        perms?.TRANSLATIONS_USERS === true;

      setCanAddTranslation(perms?.TRANSLATIONS_ADMIN === true);
      setCanUseOptions(canRead);
    } catch {
      setCanAddTranslation(false);
      setCanUseOptions(false);
    } finally {
      setPermissionLoaded(true);
    }
  }, []);

  const handlePlatformAction = () => {
    if (!dialogPlatform || !platformButtonType) return;

    switch (platformButtonType) {
      case PLATFORM_ACTION.JSON:
        handleJsonFileDownload();
        break;

      case PLATFORM_ACTION.EXCEL:
        handleExcelTemplateDownload();
        break;
      case PLATFORM_ACTION.SCRIPT:
        handleSqlScriptDownload();
        break;

      case PLATFORM_ACTION.ADD:
        openAddTranslationDialog();
        break;

      case PLATFORM_ACTION.UPDATE:
        openUpdateTranslationDialog(selectedRow);
        break;
    }

    setOpenPlatformDialog(false);
    setPlatformButtonType(null);
  };

  const openAddTranslationDialog = () => {
    setTranslationDialogTitle(t("addTranslation"));
    setTranslationDialogButton(t("formAction.add"));
    setDialogPlatform(PLATFORM.WEB);
    setForm({
      translationKey: "",
      english: "",
      japanease: "",
      spanish: "",
      portuguese: "",
      arabic: "",
      urdu: "",
      thai: "",
      french: "",
      vietnamese: "",
      lao: "",
      khmer: "",
      russian: "",
      isVerified: false,
    });
    setOpenTranslationDialog(true);
  };

  const openUpdateTranslationDialog = (row: any) => {
    if (!row) return;
    setEditingId(row.translationId);
    setTranslationDialogTitle(t("updateTranslation"));
    setTranslationDialogButton(t("formAction.update"));
    setDialogPlatform(row.platform);

    setForm({
      translationKey: row.translationKey,
      english: row.english || "",
      japanease: row.japanease || "",
      spanish: row.spanish || "",
      portuguese: row.portuguese || "",
      arabic: row.arabic || "",
      urdu: row.urdu || "",
      thai: row.thai || "",
      french: row.french || "",
      vietnamese: row.vietnamese || "",
      lao: row.lao || "",
      khmer: row.khmer || "",
      russian: row.russian || "",
      isVerified: row.isVerified || false,
    });

    setOpenTranslationDialog(true);
  };

  const buildFileDateStamp = (userFormat: string) => {
    const today = new Date();
    const formatted = format(today, userFormat);
    return formatted.replace(/\D/g, "");
  };

  const handleExcelTemplateDownload = async () => {
    try {
      store.dispatch(setIsLoading(true));

      const dateStamp = buildFileDateStamp(timeFormat);

      const safeName = `${t("translations")}_${dateStamp}`;

      const res = await apiDownloadTranslationsXlsx("WEB", safeName);

      if (!res?.data) throw new Error("Empty XLSX buffer");

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}.xlsx`;
      a.click();

      URL.revokeObjectURL(url);

      store.dispatch(setShowAlert(t("translationsExcelDownloaded")));
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.details?.customMessage ||
        err?.response?.data?.error?.localizedMessage ||
        err?.response?.data?.error?.message ||
        t("translationsExcelDownloadedFailed");

      store.dispatch(setShowAlert(message));
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  const handleJsonFileDownload = async () => {
    try {
      store.dispatch(setIsLoading(true));
      const res = await apiDownloadTranslationsZip(dialogPlatform);
      if (!res?.data) {
        throw new Error("Empty ZIP stream");
      }
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${t("translations")}_${dialogPlatform}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      store.dispatch(setShowAlert(t("translationsZipDownloaded")));
    } catch (err: any) {
      console.error("ZIP download failed", err);
      const message =
        err?.response?.data?.error?.details?.customMessage ||
        err?.response?.data?.error?.localizedMessage ||
        err?.response?.data?.error?.message ||
        t("translationsZipDownloadedFailed");

      store.dispatch(setShowAlert(message));
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  const handleSqlScriptDownload = async () => {
    try {
      store.dispatch(setIsLoading(true));

      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const offsetMinutes = new Date().getTimezoneOffset();
      const offsetHours = Math.abs(offsetMinutes / 60)
        .toString()
        .padStart(2, "0");
      const offsetSign = offsetMinutes <= 0 ? "+" : "-";
      const utcOffset = `${offsetSign}${offsetHours}:00`;

      const payloadWeb = {
        platform: PLATFORM.WEB,
        clientTimeZone: timeZone,
        clientUtcOffset: utcOffset,
        clientTimeFormat: timeFormat,
        languages: { en, ja, es, pt, ar, ur, th, fr, vi, lo, km, ru },
      };

      const payloadAPI = {
        clientTimeZone: timeZone,
        clientUtcOffset: utcOffset,
        clientTimeFormat: timeFormat,
      };

      if (dialogPlatform === PLATFORM.DB) {
        await apiImportDBTranslations();

        store.dispatch(setShowAlert(t("translationsAddedSuccessfully")));

        fetchTranslations();
        setOpenPlatformDialog(false);
        return;
      }

      const dateStamp = buildFileDateStamp(timeFormat);
      const safeName = `${t(
        "translationsSqlQueries",
      )}_${dialogPlatform}_${dateStamp}`;

      const res =
        dialogPlatform === PLATFORM.API
          ? await apiDownloadTranslationsSqlApi(payloadAPI)
          : dialogPlatform === PLATFORM.MOBILE
          ? await apiDownloadTranslationsSqlMobile(payloadAPI)
          : await apiDownloadTranslationsSqlWeb(dialogPlatform, payloadWeb);

      if (!res?.data) throw new Error("Empty SQL buffer");

      const blob = new Blob([res.data], { type: "text/plain" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}.txt`;
      a.click();

      URL.revokeObjectURL(url);

      fetchTranslations();
      setOpenPlatformDialog(false);
      store.dispatch(setShowAlert(t("translationsSqlDownloaded")));
    } catch (err: any) {
      if (dialogPlatform === PLATFORM.DB) {
        store.dispatch(setShowAlert(t("translationsFailed")));
      } else {
        const message =
          err?.response?.data?.error?.details?.customMessage ||
          err?.response?.data?.error?.localizedMessage ||
          err?.response?.data?.error?.message ||
          t("translationsSqlDownloadedFailed");

        store.dispatch(setShowAlert(message));
      }
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  const handleTranslationFieldChange = (
    field: keyof TranslationFormModel,
    value: string,
  ) => {
    setForm((prev: TranslationFormModel) => ({ ...prev, [field]: value }));
    const isNonEmpty = value.trim() !== "";
    let errorMessage: string | null = null;
    if (isNonEmpty && !REGEX.commonNameFieldRegex.test(value)) {
      errorMessage = t("invalidCharacters");
    } else if (isNonEmpty && !REGEX.emojiRegex.test(value)) {
      errorMessage = t("emojisarenotallowed");
    }
    setFormErrors(
      (prev: Record<keyof TranslationFormModel, string | null>) => ({
        ...prev,
        [field]: errorMessage,
      }),
    );
  };

  const isTranslationFormValid = () => {
    if (!dialogPlatform) return false;

    if (
      typeof form.translationKey !== "string" ||
      !form.translationKey.trim()
    ) {
      return false;
    }

    if (formErrors.translationKey) return false;

    return TRANSLATION_LANGUAGE_KEYS.some((l) => {
      const value = form[l];
      return typeof value === "string" && value.trim() !== "" && !formErrors[l];
    });
  };

  const normalizeTranslationPayload = (id?: string) => {
    const toNull = (v: string) => (v && v.trim() ? v.trim() : null);

    const VALID_PLATFORM_MAP: Record<string, string> = {
      WEB: "WEB",
      API: "API",
      MOBILE: "MOBILE",
      DB: "DB",
    };

    return {
      translationId: id || null,
      translationKey: toNull(form.translationKey),
      english: toNull(form.english),
      japanease: toNull(form.japanease),
      spanish: toNull(form.spanish),
      portuguese: toNull(form.portuguese),
      arabic: toNull(form.arabic),
      urdu: toNull(form.urdu),
      thai: toNull(form.thai),
      french: toNull(form.french),
      vietnamese: toNull(form.vietnamese),
      lao: toNull(form.lao),
      khmer: toNull(form.khmer),
      russian: toNull(form.russian),
      platform: VALID_PLATFORM_MAP[dialogPlatform],
      isVerified: form.isVerified,
    };
  };

  const handleAddTranslationSubmit = async () => {
    if (!isTranslationFormValid()) return;

    try {
      store.dispatch(setIsLoading(true));

      const payload = normalizeTranslationPayload();

      const res = await apiCreateTranslation(payload);

      store.dispatch(setShowAlert(res.data.meta.successMessage));

      setOpenTranslationDialog(false);
      shouldPreserveScroll.current = true;
      fetchTranslations();
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.details?.customMessage ||
        err?.response?.data?.error?.localizedMessage ||
        err?.response?.data?.error?.message;

      store.dispatch(setShowAlert(message));
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedRow?.translationId) return;

    try {
      store.dispatch(setIsLoading(true));

      const res = await apiDeleteTranslation(selectedRow.translationId);

      store.dispatch(setShowAlert(res.data.meta.successMessage));

      setDeleteTranslationDialog(false);
      setSelectedRow(null);
      shouldPreserveScroll.current = true;
      fetchTranslations();
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.details?.customMessage ||
        err?.response?.data?.error?.localizedMessage ||
        err?.response?.data?.error?.message;

      store.dispatch(setShowAlert(message));
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  const handleUpdateTranslationSubmit = async () => {
    if (!isTranslationFormValid() || !editingId) return;

    try {
      store.dispatch(setIsLoading(true));

      const payload = normalizeTranslationPayload(editingId);

      const res = await apiUpdateTranslation(payload);

      store.dispatch(setShowAlert(res.data.meta.successMessage));

      setOpenTranslationDialog(false);
      setEditingId(null);
      shouldPreserveScroll.current = true;
      fetchTranslations();
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.details?.customMessage ||
        err?.response?.data?.error?.localizedMessage ||
        err?.response?.data?.error?.message;

      store.dispatch(setShowAlert(message));
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  const handleInputClick = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    handleClose();
  };

  const parseBoolean = (value: any): boolean => {
    if (value === true || value === 1) return true;

    if (typeof value === "string") {
      const v = value.trim().toLowerCase();
      return v === "yes" || v === "true" || v === "1";
    }

    return false;
  };

  const markDuplicateRows = (rows: any[]) => {
    const seenKey: Record<string, Set<string>> = {};
    const seenLang: Record<string, Record<string, Set<string>>> = {};

    return rows.map((row) => {
      let isDuplicate = false;
      const errors = [...(row._errors || [])];

      const platform = (row.platform || "").trim();
      const key = (row.translationKey || "").trim().toLowerCase();

      if (!platform) {
        return { ...row, _isDuplicate: false, _errors: errors };
      }

      if (!seenKey[platform]) {
        seenKey[platform] = new Set();
      }

      if (key) {
        if (seenKey[platform].has(key)) {
          isDuplicate = true;
          errors.push("Duplicate Translation Key in same platform");
        } else {
          seenKey[platform].add(key);
        }
      }
      const shouldCheckLanguageDuplicates =
        platform !== "DB" && platform !== "MOBILE";
      if (shouldCheckLanguageDuplicates) {
        const languageFields = ["english"];

        if (!seenLang[platform]) {
          seenLang[platform] = {};
        }

        languageFields.forEach((field) => {
          const val = (row[field] || "").trim().toLowerCase();
          if (!val) return;

          if (!seenLang[platform][field]) {
            seenLang[platform][field] = new Set();
          }

          if (seenLang[platform][field].has(val)) {
            isDuplicate = true;
            errors.push(`Duplicate ${field} value in same platform`);
          } else {
            seenLang[platform][field].add(val);
          }
        });
      }

      return {
        ...row,
        _isDuplicate: isDuplicate,
        _errors: errors,
      };
    });
  };

  const handleFileExcelRead = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validExtensions = [".xlsx", ".xls"];
    const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();

    if (!validExtensions.includes(ext)) {
      store.dispatch(
        setShowAlert(t("InvalidfiletypePleaseuploadanExcelfilexlsxorxls")),
      );
      return;
    }

    try {
      store.dispatch(setIsLoading(true));

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });

      const allRows: any[] = [];

      const PLATFORM_IDS = PLATFORM_OPTIONS.map((p) => p.id);
      workbook.SheetNames.forEach((name) => {
        if (!PLATFORM_IDS.includes(name)) return;

        const sheet = workbook.Sheets[name];

        const headerRow = XLSX.utils.sheet_to_json<any>(sheet, {
          header: 1,
          defval: "",
        })[0] as string[];

        if (!headerRow) return;

        const hasAllRequiredColumns = REQUIRED_COLUMNS.every((col) =>
          headerRow.includes(col),
        );

        if (!hasAllRequiredColumns) {
          console.warn(
            `Skipping sheet "${name}" due to missing required columns`,
          );
          return;
        }

        const rows = XLSX.utils.sheet_to_json<any>(sheet, {
          defval: "",
          raw: false,
        });

        rows.forEach((r, index) => {
          const rowErrors: string[] = [];

          if (!r["Translation ID"]) {
            rowErrors.push(t("translation.translationId"));
          }

          if (!r["Translation Key"]) {
            rowErrors.push(t("translation.translationKey"));
          }

          if (!name) {
            rowErrors.push(t("translation.missingPlatform"));
          }

          const rawVerified = (r["Is Verified"] || "").toString().trim();

          if (!rawVerified) {
            rowErrors.push(t("translation.missingIsVerified"));
          } else if (!["yes", "no"].includes(rawVerified.toLowerCase())) {
            rowErrors.push(t("translation.isVerifiedMustBeYesOrNo"));
          }

          allRows.push({
            id: `${name}-${index}`,
            translationId: r["Translation ID"] || null,
            platform: r["Platform"] || name,
            translationKey: r["Translation Key"],
            english: r["English"],
            japanease: r["Japanease"],
            spanish: r["Spanish"],
            portuguese: r["Portuguese"],
            arabic: r["Arabic"],
            urdu: r["Urdu"],
            thai: r["Thai"],
            french: r["French"],
            vietnamese: r["Vietnamese"],
            lao: r["Lao"],
            khmer: r["Khmer"],
            russian: r["Russian"],
            isVerified: parseBoolean(r["Is Verified"]),
          });
        });
      });

      const validated = markDuplicateRows(allRows);
      setBulkPreviewRows(validated);
      setBulkTranslationsPreviewOpen(true);
      setBulkUpdateTranslationsInfo(false);
    } catch (err) {
      console.error(err);
      store.dispatch(setShowAlert("Failed to read Excel file"));
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  const handleBulkConfirmUpdate = async () => {
    if (!filteredBulkRows.length || hasDuplicateRows) return;

    try {
      store.dispatch(setIsLoading(true));

      const payload = {
        translations: filteredBulkRows.map((row) => ({
          translationId: row.translationId,
          translationKey: row.translationKey?.trim() || null,
          english: row.english?.trim() || null,
          japanease: row.japanease?.trim() || null,
          spanish: row.spanish?.trim() || null,
          portuguese: row.portuguese?.trim() || null,
          arabic: row.arabic?.trim() || null,
          urdu: row.urdu?.trim() || null,
          thai: row.thai?.trim() || null,
          french: row.french?.trim() || null,
          vietnamese: row.vietnamese?.trim() || null,
          lao: row.lao?.trim() || null,
          khmer: row.khmer?.trim() || null,
          russian: row.russian?.trim() || null,
          platform: row.platform,
          isVerified: row.isVerified,
        })),
      };

      const res = await apiBulkUpdateTranslations(payload);
      const result = res.data?.data;

      store.dispatch(
        setShowAlert(
          `${res.data.meta.successMessage} (${t("bulkUpdateSummary", {
            total: result.total,
            passed: result.passedCount,
            failed: result.failedCount,
          })})`,
        ),
      );

      if (result.failedRows?.length) {
        const failedMap = new Map(
          result.failedRows.map((r: any) => [
            `${r.translationId}|${r.translationKey}`,
            r.reason,
          ]),
        );

        setBulkPreviewRows((prev) =>
          prev
            .filter((row) =>
              failedMap.has(`${row.translationId}|${row.translationKey}`),
            )
            .map((row) => ({
              ...row,
              _isFailed: true,
              _failureReason: failedMap.get(
                `${row.translationId}|${row.translationKey}`,
              ),
            })),
        );

        setBulkUpdateFailedCount(result.failedCount);
      } else {
        setBulkUpdateFailedCount(0);
        setBulkTranslationsPreviewOpen(false);
        setBulkSearch("");
        fetchTranslations();
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.details?.customMessage ||
        err?.response?.data?.error?.localizedMessage ||
        err?.response?.data?.error?.message;

      store.dispatch(setShowAlert(message));
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  useEffect(() => {
    if (!permissionLoaded) return;
    if (!canUseOptions) return;

    fetchTranslations();
  }, [permissionLoaded, canUseOptions, fetchTranslations]);

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const res = await getUser();
        const { data } = res.data;
        if (!data) return;
        if (data.timeFormat) {
          setTimeFormat(data.timeFormat);
        }
        await fetchPermissions();
      } catch (error) {
        console.error("Failed to load Me:", error);
      } finally {
        resetIsContinuousLoading();
        store.dispatch(setIsLoading(false));
      }
    };

    fetchMe();
  }, [id, fetchPermissions]);

  useEffect(() => {
    if (searchInput === "" && searchText !== "") {
      setPage(1);
      setSearchText("");
    }
  }, [searchInput, searchText]);

  useEffect(() => {
    if (rows.length > 0 && savedScrollPosition.current > 0) {
      const timer = setTimeout(() => {
        const tableContainer = document.querySelector(
          ".MuiTableContainer-root",
        ) as HTMLElement;
        if (tableContainer) {
          tableContainer.scrollTop = savedScrollPosition.current;
          savedScrollPosition.current = 0;
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [rows]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Helmet title={t("metaInfo.manageTranslations")} />

      <Box
        className="container"
        sx={{ mx: { xs: 2, sm: 3, md: 5, xl: "auto" }, px: "24px" }}
      >
        <Box sx={{ py: 4, margin: "auto" }}>
          <Grid container>
            {canUseOptions && (
              <Grid item xs={12}>
                <PageTitle title="translations" className="mb-25" />
              </Grid>
            )}

            {canUseOptions && (
              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                lg={6}
                sx={{ mt: { xs: 1, sm: 3 } }}
              >
                <FormControl
                  size="small"
                  fullWidth={isSmallScreen}
                  sx={{ minWidth: { sm: 180 } }}
                >
                  <InputLabel id="platForm-label">
                    {t("announcementTable.platform")}
                  </InputLabel>
                  <Select
                    labelId="platForm-label"
                    label={t("announcementTable.platform")}
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                  >
                    <MenuItem value="all">{t("all")}</MenuItem>
                    {PLATFORM_OPTIONS.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={6}
              sx={{ mt: { xs: 1, sm: 3 } }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                {canUseOptions && (
                  <Button
                    fullWidth={isSmallScreen}
                    startIcon={<MoreVert />}
                    onClick={handleClick}
                    sx={{ minWidth: 170 }}
                    className="btn-add-translation"
                  >
                    {t("formAction.optionsTranslations")}
                  </Button>
                )}

                {canAddTranslation && (
                  <Button
                    fullWidth={isSmallScreen}
                    variant="contained"
                    sx={{ minWidth: 170 }}
                    className="btn-add-translation"
                    onClick={() => {
                      openAddTranslationDialog();
                    }}
                  >
                    <IconPlus />
                    <span style={{ marginLeft: 8 }}>{t("addTranslation")}</span>
                  </Button>
                )}
              </Box>
            </Grid>

            {canUseOptions && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                    mt: { xs: 1, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      maxWidth: { xs: "100%", sm: 277 },
                    }}
                  >
                    <InputText
                      ref={searchRef}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setPage(1);
                          setSearchText(searchInput.trim());
                        }
                      }}
                      placeholder={t("Customermachinenumber")}
                      className="custom-input"
                      style={{
                        width: "100%",
                        height: 50,
                        borderRadius: 30,
                        border: "1.5px solid #bcbcbc",
                        padding: "0 2rem 0 1rem",
                      }}
                    />

                    {searchInput && (
                      <button
                        className="clear-button"
                        type="button"
                        onClick={() => {
                          setSearchInput(DEFAULT_SEARCH_INPUT);
                          setSearchText(DEFAULT_SEARCH_TEXT);
                          setPage(1);
                          setTimeout(() => searchRef.current?.focus(), 0);
                        }}
                      >
                        ×
                      </button>
                    )}
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          {canUseOptions && (
            <Box sx={{ mt: 3 }}>
              <DataTable
                rowKey="translationId"
                columns={columns}
                dataSource={rows}
                textEmptyData="noTranslationsFound"
                onRequestSort={handleSort}
                loading={tableLoading}
              />

              <DataPagination
                isHidden={!rows?.length}
                className="mt-25 sp-p24"
                currentPage={pageInfo.page}
                limitPage={pageInfo.limit}
                totalPage={pageInfo.total}
                handleChangePage={handleChangePage}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl1}
        open={openMenu}
        onClose={(event, reason) => {
          if (
            (reason === "backdropClick" || reason === "escapeKeyDown") &&
            event &&
            "preventDefault" in event
          ) {
            (event as React.SyntheticEvent).preventDefault();
            (event as React.SyntheticEvent).stopPropagation();
          }
          handleClose();
        }}
      >
        {canUseOptions && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              handleClose();

              handleExcelTemplateDownload();
            }}
          >
            <DownloadOutlined sx={{ mr: 1 }} />
            {t("formAction.downloadexcel")}
          </MenuItem>
        )}

        {canUseOptions && (
          <MenuItem>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                width: "100%",
              }}
            >
              <UploadOutlined sx={{ mr: 1 }} />
              {t("formAction.uploadexcel")}
              <input
                hidden
                onClick={() => {
                  setBulkUpdateTranslationsInfo(true);
                  handleClose();
                }}
              />
            </label>
          </MenuItem>
        )}

        {canAddTranslation && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClose();
              setPlatformDialogTitle(t("downloadjson"));
              setPlatformButtonType(PLATFORM_ACTION.JSON);
              setDialogPlatform(PLATFORM.WEB);
              setOpenPlatformDialog(true);
            }}
          >
            <DownloadOutlined sx={{ mr: 1 }} />
            {t("downloadjson")}
          </MenuItem>
        )}

        {canAddTranslation && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClose();
              setPlatformDialogTitle(t("downloadSqlScriptFull"));
              setPlatformButtonType(PLATFORM_ACTION.SCRIPT);
              setDialogPlatform(PLATFORM.WEB);
              setOpenPlatformDialog(true);
            }}
          >
            <DownloadOutlined sx={{ mr: 1 }} />
            {t("downloadSqlScript")}
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={rowActionAnchor}
        open={openRowMenu}
        onClose={(event, reason) => {
          if (
            (reason === "backdropClick" || reason === "escapeKeyDown") &&
            event &&
            "preventDefault" in event
          ) {
            (event as React.SyntheticEvent).preventDefault();
            (event as React.SyntheticEvent).stopPropagation();
          }
          setRowActionAnchor(null);
        }}
      >
        {canUseOptions && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setRowActionAnchor(null);
              openUpdateTranslationDialog(selectedRow);
            }}
          >
            <ListItemIcon>
              <IconEdit />
            </ListItemIcon>
            <ListItemText primary={t("formAction.edit")} />
          </MenuItem>
        )}
        {canAddTranslation && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setRowActionAnchor(null);
              setDeleteTranslationDialog(true);
            }}
          >
            <ListItemIcon>
              <IconDelete />
            </ListItemIcon>
            <ListItemText
              primary={t("formAction.delete")}
              primaryTypographyProps={{ style: { color: "#DA093B" } }}
            />
          </MenuItem>
        )}
      </Menu>

      <DataDialog
        disableBackdropClick
        open={openPlatformDialog}
        setOpen={setOpenPlatformDialog}
        title={platformDialogTitle}
      >
        <Box sx={{ mt: 1 }}>
          {platformButtonType === PLATFORM_ACTION.SCRIPT && (
            <Box
              sx={{
                backgroundColor: "#F9FAFB",
                border: "1px solid #E5E7EB",
                borderRadius: 2,
                padding: 2,
                fontSize: 14,
                color: "#374151",
                marginBottom: "20px",
              }}
            >
              <p style={{ margin: 0 }}>{t("sqlScriptInfodescription")}</p>
            </Box>
          )}
          <FormControl fullWidth>
            <InputLabel>{t("announcementTable.platform")}</InputLabel>
            <Select
              label={t("announcementTable.platform")}
              value={dialogPlatform}
              onChange={(e) =>
                setDialogPlatform(e.target.value as PlatformType)
              }
            >
              {PLATFORM_OPTIONS.filter((p) => p.id !== "all").map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}
          >
            <Button
              className="btn btn-no-border"
              onClick={() => setOpenPlatformDialog(false)}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary"
              disabled={!dialogPlatform}
              onClick={handlePlatformAction}
            >
              {platformButtonType === "ADD" || platformButtonType === "UPDATE"
                ? t("ok")
                : t("translations.download")}
            </Button>
          </Box>
        </Box>
      </DataDialog>

      <DataDialog
        disableBackdropClick
        open={openTranslationDialog}
        setOpen={setOpenTranslationDialog}
        title={translationDialogTitle}
      >
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t("announcementTable.platform")}</InputLabel>
              <Select
                label={t("announcementTable.platform")}
                value={dialogPlatform}
                onChange={(e) =>
                  setDialogPlatform(e.target.value as PlatformType)
                }
                disabled={isUpdateMode}
              >
                {PLATFORM_OPTIONS.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              className="input-field"
              label={t("translation.key")}
              value={form.translationKey}
              onChange={(e) =>
                handleTranslationFieldChange("translationKey", e.target.value)
              }
              error={!!formErrors.translationKey}
              helperText={formErrors.translationKey}
              disabled={isUpdateMode}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!form.isVerified}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      isVerified: e.target.checked,
                    }))
                  }
                  sx={{
                    color: "#9CA3AF",
                    "&.Mui-checked": {
                      color: "#140a9a",
                    },
                  }}
                />
              }
              label={t("translation.isVerified")}
            />
          </Grid>

          {TRANSLATION_FIELDS.map((f) => (
            <Grid item xs={12} sm={f.full ? 12 : 6} key={f.key}>
              <TextField
                className="input-field"
                label={t(f.label)}
                value={form[f.key]}
                onChange={(e) =>
                  handleTranslationFieldChange(f.key, e.target.value)
                }
                error={!!formErrors[f.key]}
                helperText={formErrors[f.key]}
                inputProps={{ style: { direction: f.rtl ? "rtl" : "ltr" } }}
              />
            </Grid>
          ))}
        </Grid>

        <div
          className="flex justify-content-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => setOpenTranslationDialog(false)}
          >
            {t("dialogReport.cancel")}
          </Button>
          <Button
            className="btn btn-primary"
            disabled={!isTranslationFormValid()}
            onClick={() =>
              translationDialogButton === t("formAction.add")
                ? handleAddTranslationSubmit()
                : handleUpdateTranslationSubmit()
            }
          >
            {translationDialogButton}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("deleteTranslation")}
        open={deleteTranslationDialog}
        setOpen={setDeleteTranslationDialog}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("deleteSelectedTranslation")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => {
                setDeleteTranslationDialog(false);
              }}
              className="btn btn-no-border"
              style={{ marginRight: "10px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary bg-red"
              onClick={handleConfirmDelete}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        open={bulkUpdateTranslationsInfo}
        setOpen={setBulkUpdateTranslationsInfo}
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>{t("excelimport")}</span>
            <span
              className="fs-16 txt-gray-3"
              style={{ color: "#6B7280", marginTop: "-4px" }}
            ></span>
          </div>
        }
      >
        <div
          className="warning flex justify-content-start"
          style={{ marginTop: 0, marginBottom: "10px" }}
        >
          <IconWarningFormEdit />
          <span style={{ marginLeft: 12, flex: 1 }} className="txt-black-1">
            {t("warningUpdateItem2")}
          </span>
        </div>

        <div
          className="flex justify-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            style={{ width: "auto", marginTop: "4px" }}
            className="btn btn-no-border"
            onClick={() => {
              setBulkUpdateTranslationsInfo(false);
              handleClose();
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <label htmlFor="file-upload">
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#10069F",
                color: "#FFFFFF",
                borderRadius: "50px",
                padding: "10px 10px",
                fontSize: "14px",
                fontWeight: "normal",
                fontFamily: "Arial, sans-serif",
                letterSpacing: "0.5px",
                minWidth: "100px",
                textTransform: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                "&:hover": {
                  backgroundColor: "#253fc8",
                },
              }}
              component="span"
            >
              {t("chooseFile")}
            </Button>
            <input
              id="file-upload"
              type="file"
              accept=".xlsx, .xls"
              style={{ display: "none" }}
              onClick={handleInputClick}
              onChange={(e) => {
                handleFileExcelRead(e);
                e.target.value = "";
                handleClose();
              }}
            />
          </label>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick
        open={bulkTranslationsPreviewOpen}
        setOpen={setBulkTranslationsPreviewOpen}
        title={t("updateTranslation")}
        className="dialog-inspection-form"
      >
        {(hasDuplicateRows || bulkUpdateFailedCount > 0) && (
          <div
            style={{
              background: "#FEE2E2",
              color: "#991B1B",
              padding: "12px 16px",
              borderRadius: 8,
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              fontWeight: 500,
            }}
          >
            ⚠{" "}
            {hasDuplicateRows
              ? t("bulkDuplicateWarning", { count: duplicateCount })
              : t("bulkUpdateFailedWarning", { count: bulkUpdateFailedCount })}
          </div>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder={t("Customermachinenumber")}
          value={bulkSearch}
          onChange={(e) => setBulkSearch(e.target.value)}
          sx={{ mb: 2 }}
          className="input-field"
        />

        <div
          style={{
            width: "100%",
            height: 500,
            overflow: "auto",
            marginBottom: 20,
          }}
        >
          <DataGrid
            rows={filteredBulkRows}
            columns={bulkPreviewColumns}
            disableColumnMenu
            pagination
            paginationModel={bulkPaginationModel}
            onPaginationModelChange={setBulkPaginationModel}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            getRowClassName={(params) =>
              params.row._isFailed || params.row._isDuplicate
                ? "row-duplicate"
                : ""
            }
            sx={{
              "& .bulk-header .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                textAlign: "center",
                width: "100%",
              },
              "& .row-duplicate": {
                backgroundColor: "#ffe5e5",
                "&:hover": {
                  backgroundColor: "#ffd6d6",
                },
              },
            }}
          />
        </div>

        <div style={{ textAlign: "right", marginBottom: 10 }}>
          {t("page")} {bulkPaginationModel.page + 1}
        </div>

        <div
          className="btn-group-footer mt-8"
          style={{
            position: "sticky",
            bottom: 0,
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            background: "#fff",
            paddingTop: "16px",
            paddingBottom: "8px",
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => {
              setBulkTranslationsPreviewOpen(false);
              setBulkSearch("");
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            disabled={hasDuplicateRows}
            onClick={handleBulkConfirmUpdate}
          >
            {t("formAction.update")}
          </Button>
        </div>
      </DataDialog>
    </LocalizationProvider>
  );
}
